import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiCall } from "../api/apiCall";
import Navbar from "../components/Navbar/Navbar";
import { CompanyPolicy } from "../models/company_policy";
import { Company } from "../models/company";
import { toast } from "react-toastify";
import generateToastContainer from "../utils/ToastContainer";
import "./CompanyForm.css"; 
import TopHeader from "../components/Navbar/TopHeader";
import FeatureGate from "../components/FeatureGate";
import { useTranslation } from "../i18n/UseTranslation";
import { CategoryIcon } from "../utils/CategoryIcons";
import { useCurrentUser } from "./../expense_report/UserCurrentUser";
import ConfirmDialog from "../components/Navbar/ConfirmLogoutDialog";
import { getAuth, onAuthStateChanged } from "firebase/auth";

function CompanyPolicyForm() {
  const { t } = useTranslation();
  const translationCat = t.categoryTypes;
  const { companyUuid: paramsUuid } = useParams<{ companyUuid: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { currentUser, isLoading: isLoadingMe, isSuperAdmin } = useCurrentUser();

  const userToGlimpse = currentUser as any;

  const userSubscription = 
    userToGlimpse?.Company?.Subscription || 
    userToGlimpse?.company?.Subscription ||
    userToGlimpse?.company?.subscription;

const currentPlan = !currentUser
  ? "none"
  : userSubscription?.status === "active"
    ? userSubscription.plan_code
    : "free";

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<string | null>(null);

  const [isAuthReady, setIsAuthReady] = useState(false);

   useEffect(() => {
        const auth = getAuth(); 
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          setIsAuthReady(true);
        });
        return () => unsubscribe();
      }, []);

  const { data: allCompanies, isLoading: isLoadingCompanies } = useQuery<Company[], Error>({
    queryKey: ["fetchAllCompanies"],
    queryFn: () => apiCall<Company[]>("/companies", { method: "GET" }),
    enabled: isSuperAdmin && isAuthReady
  });

  const effectiveUuid = isSuperAdmin
    ? (paramsUuid || null)
    : (paramsUuid || userToGlimpse?.company_uuid);

  const { data: policies, isLoading: isLoadingPolicies } = useQuery<CompanyPolicy[], Error>({
    queryKey: ["fetchCompanyPolicies", effectiveUuid],
    queryFn: () => apiCall<CompanyPolicy[]>(`/companies/${effectiveUuid}/policies`),
    enabled: !!effectiveUuid && isAuthReady,
  });

  const deleteMutation = useMutation({
    mutationFn: (policyUuid: string) =>
      apiCall(`/companies/${effectiveUuid}/policies/${policyUuid}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success(t.companyPolicies.deleteSuccess);
      queryClient.invalidateQueries({ queryKey: ["fetchCompanyPolicies", effectiveUuid] });
      setSelectedPolicyUuid(null);
    },
    onError: (error: Error) => {
      toast.error(`${t.common?.error || "Erreur"} : ${error.message}`);
    },
  });

  const getTranslatedName = useCallback((itemType: string) => {
    if (!itemType) return "Autre"; 
    return translationCat[itemType.toLowerCase() as keyof typeof translationCat] || itemType;
  }, [translationCat]); 

  const sortedPolicies = useMemo(() => {
    return [...(policies ?? [])].sort((a, b) =>
      getTranslatedName(a.category.item_type).localeCompare(
        getTranslatedName(b.category.item_type)
      )
    );
  }, [policies, getTranslatedName]);

  const [selectedPolicyUuid, setSelectedPolicyUuid] = useState<string | null>(null);
  
  const selectedPolicy = useMemo(() => {
    return sortedPolicies.find((p) => p.uuid === selectedPolicyUuid) || sortedPolicies[0] || null;
  }, [sortedPolicies, selectedPolicyUuid]);

  const handleDeleteClick = (policyUuid: string) => {
    setPolicyToDelete(policyUuid);
    setIsDeleteDialogOpen(true);
  };

  const executeDelete = () => {
    if (policyToDelete) {
      deleteMutation.mutate(policyToDelete);
    }
    setIsDeleteDialogOpen(false);
    setPolicyToDelete(null);
  };

  const getVatRuleLabel = useCallback((ruleType: string) => {
    const vatRulesTranslations = t.companyPolicyForm.vatRules;
    
    switch (ruleType) {
      case "NONE":
        return vatRulesTranslations?.none || "None";
      case "MANUAL":
        return vatRulesTranslations?.manual || "Manual";
      case "SUM_LINES":
        return vatRulesTranslations?.sumLines || "Sum Lines";
      default:
        return ruleType;
    }
  }, [t]);

  const handleCompanyChange = (companyUuid: string) => {
    setSelectedPolicyUuid(null);
    if (companyUuid) {
      navigate(`/companies/${companyUuid}/policies`);
    } else {
      navigate(`/company-policies`);
    }
  };

  

  if (isLoadingMe || (isSuperAdmin && isLoadingCompanies) || (effectiveUuid && isLoadingPolicies)) {
    return (
      <div className="dashboard-wrapper">
        <Navbar />
        <TopHeader />
        <FeatureGate userPlan={currentPlan} requiredPlan="free" featureName={t.companyPolicies.featureName} minHeight={500}>
          <p className="loading-text">{t.companyPolicies.loading}</p>
        </FeatureGate>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <Navbar />
      <TopHeader />

      <div className="dashboard-content">
        <FeatureGate userPlan={currentPlan} requiredPlan="free" featureName={t.companyPolicies.featureName} minHeight={500}>
          <div className="policy-main-container">
            
            <div className="policy-header-row">
              <h1 className="policy-page-title">{t.companyPolicies.title}</h1>
              {effectiveUuid && (
                <button 
                  type="button"
                  className="add-policy-submit" 
                  onClick={() => navigate(`/companies/${effectiveUuid}/policies/add`)}
                >
                  {t.companyPolicies.addBtn}
                </button>
              )}
            </div>

            {isSuperAdmin && (
              <div className="policy-company-selector">
                <label className="account-field-label">
                  {t.companyPolicies.selectCompany}
                </label>
                <select
                  className="account-select"
                  value={paramsUuid ?? ""}
                  onChange={(e) => handleCompanyChange(e.target.value)}
                >
                  <option value="">{t.companyPolicies.chooseCompany}</option>
                  {(allCompanies ?? []).map((company) => (
                    <option key={company.uuid} value={company.uuid}>
                      {" " + company.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!effectiveUuid ? (
              <p className="no-category-selected">{t.companyPolicies.selectCategory}</p>
            ) : (
              <div className="policy-layout-grid">
                
                <div className="policy-sidebar">
                  {sortedPolicies.map((policy) => {
                    const isSelected = selectedPolicy?.uuid === policy.uuid;
                    return (
                      <div
                        key={policy.uuid}
                        onClick={() => setSelectedPolicyUuid(policy.uuid)}
                        className={`category-item ${isSelected ? 'selected' : ''}`}
                      >
                        <div className="category-icon">
                          <CategoryIcon itemType={policy.category.item_type} size={16} />
                        </div>
                        <span className="category-name">
                          {getTranslatedName(policy.category.item_type)}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="policy-details-pane">
                  {selectedPolicy ? (
                    <>
                      <div className="details-header">
                        <h2>{getTranslatedName(selectedPolicy.category.item_type)}</h2>
                        <div className="header-actions">
                          <button 
                            onClick={() => handleDeleteClick(selectedPolicy.uuid)}
                            className="policy-delete-btn"
                            disabled={deleteMutation.isPending}
                          >
                            {deleteMutation.isPending ? t.companyPolicies.deleting : t.companyPolicies.deleteBtn}
                          </button>
                          <button 
                            onClick={() => navigate(`/companies/${effectiveUuid}/policies/${selectedPolicy.uuid}/edit`)}
                            className="policy-edit-btn"
                          >
                            {t.companyPolicies.editBtn}
                          </button>
                        </div>
                      </div>
                      

                      <div className="details-grid">
                        
                         <DetailBox label={t.companyPolicies.status} value={selectedPolicy.allowed ? t.companyPolicies.allowed : t.companyPolicies.forbidden} />
                         <DetailBox label={t.companyPolicies.dailyLimit} value={selectedPolicy.spending_limit ? `${selectedPolicy.spending_limit}€` : t.companyPolicies.noLimit} />
                         <DetailBox label={t.companyPolicies.vatRule} value={getVatRuleLabel(selectedPolicy.vat_rule_type)} />
                         <DetailBox label={t.companyPolicies.receiptRequired} value={selectedPolicy.requires_receipt ? t.companyPolicies.yes : t.companyPolicies.no} />
                         <DetailBox label={t.companyPolicies.manualVat} value={selectedPolicy.allows_manual_vat ? t.companyPolicies.yes : t.companyPolicies.no} />
                         <DetailBox label={t.companyPolicies.active} value={selectedPolicy.is_active ? t.companyPolicies.yes : t.companyPolicies.no} />
                      </div>
                    </>
                  ) : (
                    <p className="no-category-selected">{t.companyPolicies.selectCategory}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Modale de confirmation de suppression */}
          <ConfirmDialog
            isOpen={isDeleteDialogOpen}
            title={t.common?.deleteConfirmTitle || "Confirmer la suppression"}
            content={t.companyPolicies.deleteConfirm || "Voulez-vous vraiment supprimer cette politique ?"}
            cancelText={t.common?.cancel || "Annuler"}
            confirmText={t.companyPolicies.deleteBtn || "Supprimer"}
            onCancel={() => {
              setIsDeleteDialogOpen(false);
              setPolicyToDelete(null);
            }}
            onConfirm={executeDelete}
            variant="danger"
          />

          {generateToastContainer()}
        </FeatureGate>
      </div>
    </div>
  );
}

function DetailBox({ label, value }: { label: string; value: any }) {
  return (
    <div className="detail-box">
      <small>{label}</small>
      <span>{value}</span>
    </div>
  );
}

export default CompanyPolicyForm;